import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ALL_COUNTRIES, flagFor, countryByCode } from "@/lib/allCountries";

/**
 * Searchable country picker covering every ISO-3166-1 country. Backed by Shadcn's
 * Command + Popover (cmdk under the hood) so typing 3 letters narrows the list in
 * real time — much friendlier than a 196-row native <select>.
 *
 * Props:
 *   value          — ISO-3166-1 alpha-2 code (e.g. "FR"). Empty string = unset.
 *   onChange(code) — fires when the user picks a row or clears.
 *   placeholder    — trigger text when nothing is selected.
 *   testId         — base data-testid for trigger + clear button.
 *   disabled       — read-only mode.
 */
export default function CountryCombobox({
  value,
  onChange,
  placeholder = "Select a country…",
  testId = "country-combobox",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const current = countryByCode(value);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between rounded-xl font-normal"
          data-testid={testId}
        >
          <span className="truncate text-left">
            {current ? (
              <>
                <span className="mr-2" aria-hidden="true">{flagFor(current.code)}</span>
                {current.name}
              </>
            ) : (
              <span className="text-neutral-500">{placeholder}</span>
            )}
          </span>
          {value && !disabled ? (
            <X
              className="ml-2 h-4 w-4 shrink-0 opacity-60 hover:opacity-100"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange?.(""); }}
              data-testid={`${testId}-clear`}
            />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue is "FR France" — match against either the code or the name so
            // typing "uk" finds United Kingdom and typing "DE" jumps to Germany.
            if (!search) return 1;
            const q = search.toLowerCase();
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search country…" data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {ALL_COUNTRIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.code} ${c.name}`}
                  onSelect={() => {
                    onChange?.(c.code);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${c.code}`}
                >
                  <span className="mr-2" aria-hidden="true">{flagFor(c.code)}</span>
                  <span className="flex-1">{c.name}</span>
                  <Check className={cn("h-4 w-4", value === c.code ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
